import {
  DateConstraint,
  IFieldInstance,
  IncidentType,
} from "../../types/fieldInstance.interface";
import formData from "./form.json";
import { compareStrings } from "./viewModelMethods";

export function toArray(string: string, separator: string = ",") {
  return string
    .split(separator)
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

export function getTabArray(tab: string, incidentTypes: string[]) {
  let tabArray: any[] = [];
  if (tab.includes(",")) {
    const separatedArray = tab.match(/\[.{0,100}?\]/g);
    // regex reduces vulnerability to super-linear runtime by limiting the number of matches
    if (separatedArray === null) return tabArray;
    const tabs = separatedArray
      .map((el) => {
        // eslint-disable-next-line
        const array = el.replace(/[\[\]]/g, "").split("; ");
        const elIncidentTypes = [];
        if (array[1].includes(",")) {
          elIncidentTypes.push(...array[1].split(", "));
        } else {
          elIncidentTypes.push(array[1]);
        }

        return elIncidentTypes.length === 1
          ? [[elIncidentTypes[0], array[0]]]
          : elIncidentTypes.map((v) => [v, array[0]]);
      })
      .flat()
      .filter((el) => incidentTypes.includes(el[0]));
    tabArray = tabs;
  } else {
    tabArray = tab ? incidentTypes.map((type) => [type, tab]) : [];
  }
  return tabArray;
}

export function getCondition(condition: string) {
  let conditionToWatch;
  const conditionStr = condition.split("=")[1];
  if (conditionStr.includes(",")) {
    conditionToWatch = toArray(conditionStr);
  } else {
    conditionToWatch = conditionStr.trim();
  }
  return conditionToWatch;
}

function convertToBoolean(
  current: { [x: string]: any },
  property: string | number
) {
  return !!(current[property] && compareStrings(current[property], "yes"));
}

export function convertToCamelCase(fieldName: string) {
  const strToArray = fieldName.split("_");
  return strToArray
    .map((part, index) => {
      if (index === 0) {
        return part.charAt(0).toLowerCase() + part.slice(1);
      }
      if (index === strToArray.length - 1) {
        return "";
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("");
}

export function getFieldConstraint(
  stringVariable: string
): DateConstraint | null {
  const regex =
    /^(<=|>=|<|>|=)\s*\[(creation_date|Incident_Date_Time__c|\w+)\]$/;
  const match = stringVariable.match(regex);

  if (!match) {
    return null;
  }

  const operator = match[1];
  const variable = match[2];

  const comparison: DateConstraint = {};

  switch (operator) {
    case "<=":
      comparison.max = variable;
      break;
    case ">=":
      comparison.min = variable;
      break;
    case "<":
      comparison.max = variable;
      break;
    case ">":
      comparison.min = variable;
      break;
  }

  return comparison;
}

type Acc = { [key: string]: IFieldInstance[] };

const transformData = (data: any) => {
  return data.reduce((acc: Acc, current: { [x: string]: any }) => {
    const instance: IFieldInstance = {
      id: current.id.toString(),
      fieldName: current.fieldname || "",
      parentFieldId: current.iparentfield
        ? current.iparentfield.toString()
        : "",
      childFieldIds: current.childfields ? toArray(current.childfields) : [],
      tab: current.tab
        ? getTabArray(current.tab, toArray(current.incidentTypes))
        : [],
      incidentType: current.incidentTypes
        ? (toArray(current.incidentTypes) as IncidentType[])
        : [],
      section: current.section,
      fieldLabel: current.fieldlabel,
      condition: current.displayWhen ? getCondition(current.displayWhen) : "",
      fieldType: current.fieldType,
      dataType: current.dataType,
      unit: current.unit ? current.unit : "",
      values: current.values ? toArray(current.values) : [],
      valueLabels: current.valueLabels ? toArray(current.valueLabels, "|") : [],
      required: convertToBoolean(current, "required"),
      requiredForNewIncidentOnly: convertToBoolean(
        current,
        "requiredForNewIncidentOnly"
      ),
      searchable: convertToBoolean(current, "searchable"),
      tableLabel: current.tablelabel || "",
      qapi: convertToBoolean(current, "nonqapiHidden"),
      constraint: current.constraints
        ? getFieldConstraint(current.constraints)
        : null,
      askConfirmation: convertToBoolean(current, "askConfirmation"),
      "data-testid": current.fieldname
        ? current.fieldname.replace(/__/g, "_")
        : `field_${current.id}`,
    };
    if (current.fieldname) {
      if (!acc[current.fieldname]) {
        acc[current.fieldname] = [instance];
      } else {
        acc[current.fieldname].push(instance);
      }
    } else {
      if (!acc["unnamed"]) {
        acc["unnamed"] = [instance];
      } else acc["unnamed"].push(instance);
    }
    return acc;
  }, {});
};

export const transformedData = transformData(formData);

export const transformRLData = (rLformFields: any) => {
  const transformedRLFormFields: any = {};
  for (const [key, value] of Object.entries(rLformFields)) {
    transformedRLFormFields[key] = transformData(value);
  }
  return transformedRLFormFields;
};
